import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from 'react-redux';
import { Spin } from 'antd';
import { toast, Bounce } from 'react-toastify';

import useUploadHook from "components/uploadHook";
import { Button } from "components/Button";
import { applyJobInternalApi } from "api/services/jobs";
import EssFullWidthModal from "components/modelpopup/baseModal";

// Add import for PhoneInput
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const ReferralApplicationModal = ({ visible, onCancel, selectedJob, onSuccess }) => {
  const [nationalityOption, setNationalityOption] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const { nationality, nationalityLoading } = useSelector(
    (state) => state.common
  );

  const {
    uploadLoading,
    handleFile
  } = useUploadHook();

  const applicationSchema = yup.object().shape({
    first_name: yup.string().required("First name is required"),
    last_name: yup.string().required("Last name is required"),
    email: yup
      .string()
      .email("Invalid email format")
      .required("Email is required"),
    phone: yup
      .string()
      .required("Phone number is required")
      .min(6, "Phone number is too short"),
    resume: yup.string().required("Resume is required"),
    note_by_user: yup.string().required("Please explain why this position is suitable for this person"),
    residence_status: yup
      .string()
      .required("Residency status is required"),
    nationality: yup
      .number()
      .transform((value, originalValue) => {
        if (originalValue === '' || originalValue === undefined || originalValue === null) {
          return undefined;
        }
        const num = Number(originalValue);
        return isNaN(num) ? undefined : num;
      })
      .typeError("Please select a nationality")
      .required("Nationality is required"),
    gender: yup
      .string()
      .required("Gender is required"),
  });

  useEffect(() => {
    let _nationality = nationality.map((item) => {
      return {
        label: item.name,
        value: item.id,
      };
    });
    setNationalityOption(_nationality);
  }, [nationality]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    setError,
    control,
  } = useForm({
    resolver: yupResolver(applicationSchema),
    defaultValues: {},
  });

  const fileUploadHandler = async(e) => {
    const file = e.target.files[0];
    
    // Check file size (5MB limit)
    if (file && file.size > 5 * 1024 * 1024) {
      toast.error('File size exceeds 5MB limit. Please upload a smaller file.', {
        position: "top-right",
        autoClose: 5000,
      });
      e.target.value = '';
      return;
    }
    
    const url = await handleFile(e);
    setValue("resume", url);
  };

  const saveData = async (data) => {
    // Validate nationality is a number
    if (isNaN(parseInt(data.nationality))) {
      setError('nationality', {
        type: 'manual',
        message: 'Please select a valid nationality'
      });
      return;
    }
    
    // Validate phone number
    if (!data.phone || data.phone.length < 6) {
      setError('phone', {
        type: 'manual',
        message: 'Please enter a valid phone number'
      });
      return;
    }
    
    setSubmitting(true);
    
    // Format the data according to API requirements
    const formattedData = {
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      phone: data.phone,
      note_by_user: data.note_by_user, // This replaces cover_letter for referrals
      resume: data.resume,
      residence_status: data.residence_status,
      gender: data.gender,
      nationality: parseInt(data.nationality),
      job: selectedJob.id,
    };

    try {
      await applyJobInternalApi(formattedData);
      toast.success('Your referral has been submitted successfully!', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
      reset();
      onCancel();
      
      // Call the success callback to refresh the resume list
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error submitting referral:', error);
      if (error.response?.data) {
        Object.entries(error.response.data).forEach(([k, v]) => {
          setError(k, {
            type: 'manual',
            message: Array.isArray(v) ? v.join(", ") : v
          });
        });
      }
      toast.error(error?.response?.data?.error || 'There was a problem submitting your referral. Please try again.', {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  return (
    <EssFullWidthModal
      title={`Refer a Friend - ${selectedJob?.title || 'Job'}`}
      open={visible}
      onCancel={handleCancel}
      mask={false}
      className="full-width-modal"
    >
      <div className="modal-content">
        <div className="modal-body">
          <div className="job-info mb-4 p-3 bg-light rounded">
            <h6 className="mb-2">{selectedJob?.title}</h6>
            <p className="mb-1 text-muted">{selectedJob?.department?.name}</p>
            {selectedJob?.location && (
              <p className="mb-0 text-muted">
                <i className="fa fa-map-marker me-1"></i>
                {selectedJob.location}
              </p>
            )}
          </div>

          <form>
        <div className="row">
          <div className="col-md-6">
            <div className="input-block mb-3">
              <label className="col-form-label">
                First Name <span className="text-danger">*</span>
              </label>
              <input 
                {...register("first_name")} 
                className={`form-control ${errors?.first_name ? 'is-invalid' : ''}`} 
                type="text" 
                placeholder="Enter first name" 
              />
              {errors?.first_name && (
                <div className="invalid-feedback d-block">
                  {errors.first_name.message}
                </div>
              )}
            </div>
          </div>
          <div className="col-md-6">
            <div className="input-block mb-3">
              <label className="col-form-label">
                Last Name <span className="text-danger">*</span>
              </label>
              <input 
                {...register("last_name")} 
                className={`form-control ${errors?.last_name ? 'is-invalid' : ''}`} 
                type="text" 
                placeholder="Enter last name" 
              />
              {errors?.last_name && (
                <div className="invalid-feedback d-block">
                  {errors.last_name.message}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="row">
          <div className="col-md-6">
            <div className="input-block mb-3">
              <label className="col-form-label">
                Phone Number <span className="text-danger">*</span>
              </label>
              <Controller
                name="phone"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <PhoneInput
                    country={'ae'} // Default country (UAE)
                    value={value}
                    onChange={onChange}
                    inputClass={`form-control ${errors?.phone ? 'is-invalid' : ''}`}
                    containerClass="phone-input-container"
                    buttonClass={errors?.phone ? 'phone-dropdown-error' : ''}
                    inputProps={{
                      name: 'phone',
                      required: true,
                    }}
                  />
                )}
              />
              {errors?.phone && (
                <div className="invalid-feedback d-block">
                  {errors.phone.message}
                </div>
              )}
            </div>
          </div>
          <div className="col-md-6">
            <div className="input-block mb-3">
              <label className="col-form-label">
                Email Address <span className="text-danger">*</span>
              </label>
              <input
                {...register("email")}
                className={`form-control ${errors?.email ? 'is-invalid' : ''}`}
                type="email"
                placeholder="Enter email address"
              />
              {errors?.email && (
                <div className="invalid-feedback d-block">
                  {errors.email.message}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="row">
          <div className="col-md-6">
            <div className="input-block mb-3">
              <label className="col-form-label">
                Nationality <span className="text-danger">*</span>
              </label>
              <select 
                {...register("nationality")} 
                className={`form-select ${errors?.nationality ? 'is-invalid' : ''}`}
              >
                <option value="">Select nationality</option>
                {nationalityOption.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors?.nationality && (
                <div className="invalid-feedback d-block">
                  {errors.nationality.message}
                </div>
              )}
            </div>
          </div>
          <div className="col-md-6">
            <div className="input-block mb-3">
              <label className="col-form-label">
                Residency Status <span className="text-danger">*</span>
              </label>
              <select 
                {...register("residence_status")} 
                className={`form-select ${errors?.residence_status ? 'is-invalid' : ''}`}
              >
                <option value="">Select residency status</option>
                <option value="uae citizen">Citizen</option>
                <option value="resident">Resident</option>
                <option value="dependent">Dependent</option>
                <option value="visit">Visit</option>
              </select>
              {errors?.residence_status && (
                <div className="invalid-feedback d-block">
                  {errors.residence_status.message}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="row">
          <div className="col-md-6">
            <div className="input-block mb-3">
              <label className="col-form-label">
                Gender <span className="text-danger">*</span>
              </label>
              <select 
                {...register("gender")} 
                className={`form-select ${errors?.gender ? 'is-invalid' : ''}`}
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
              {errors?.gender && (
                <div className="invalid-feedback d-block">
                  {errors.gender.message}
                </div>
              )}
            </div>
          </div>
          <div className="col-md-6">
            <div className="input-block mb-3">
              <label className="col-form-label">
                Upload CV <span className="text-danger">*</span> {uploadLoading && <i className="fa fa-spinner fa-spin"></i>}
              </label>
              <input
                type="file"
                className={`form-control ${errors?.resume ? 'is-invalid' : ''}`}
                accept=".pdf, .docx"
                onChange={fileUploadHandler}
                disabled={uploadLoading}
              />
              <small className="form-text text-muted">Accepted formats: PDF, DOCX (Max size: 5MB)</small>
              {errors?.resume && (
                <div className="invalid-feedback d-block">
                  {errors.resume.message}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="input-block mb-4">
          <label className="col-form-label">
            Why do you think this position is suitable for this person? <span className="text-danger">*</span>
          </label>
          <textarea
            {...register("note_by_user")}
            className={`form-control ${errors?.note_by_user ? 'is-invalid' : ''}`}
            rows="4"
            placeholder="Please explain why you think this person would be a good fit for this position..."
          ></textarea>
          {errors?.note_by_user && (
            <div className="invalid-feedback d-block">
              {errors.note_by_user.message}
            </div>
          )}
        </div>
        
        <div className="submit-section text-end">
          <Button
            onClick={handleCancel}
            label="Cancel"
            className="btn btn-outline-secondary me-2"
          />
          <Button
            onClick={(e) => {
              e.preventDefault();
              handleSubmit(saveData)();
            }}
            label={submitting ? <><Spin size="small" className="me-2" /> Submitting...</> : "Submit Referral"}
            className="btn btn-primary"
            disabled={submitting}
          />
        </div>
          </form>
        </div>
      </div>
      
      {/* Add custom CSS for phone input */}
      <style jsx>{`
        .phone-input-container {
          width: 100%;
        }
        .phone-input-container .form-control {
          width: 100% !important;
          padding-left: 50px !important;
        }
        .phone-dropdown-error {
          border-color: #dc3545 !important;
        }
      `}</style>
    </EssFullWidthModal>
  );
};

export default ReferralApplicationModal;
